import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { isFinishedTrackStatus } from './status';
import { parsePodPages } from './podParser';
import { rankStefBlCandidates } from './stefBl';

/*
 * Le paramètre de version force les navigateurs ayant mémorisé l'ancienne
 * réponse OVH sans type MIME JavaScript à télécharger de nouveau le worker.
 */
const PDF_WORKER_CACHE_VERSION = '20260730-1';
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${workerUrl}?v=${PDF_WORKER_CACHE_VERSION}`;

const DAY_MS = 86_400_000;
const LINE_TOLERANCE = 3.5;

const clean = (value) =>
  String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalize = (value) =>
  clean(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const normalizeHeader = (value) =>
  normalize(value).replace(/[^a-z0-9]/g, '');

function toIsoDate(year, month, day) {
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseFrenchDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toIsoDate(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate()
    );
  }

  if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value);
    return date ? toIsoDate(date.y, date.m, date.d) : null;
  }

  const text = clean(value);
  const frenchMatch = text.match(
    /\b(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2}|\d{4})\b/
  );

  if (frenchMatch) {
    const year =
      frenchMatch[3].length === 2
        ? Number(`20${frenchMatch[3]}`)
        : Number(frenchMatch[3]);

    return toIsoDate(
      year,
      Number(frenchMatch[2]),
      Number(frenchMatch[1])
    );
  }

  const isoMatch = text.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);

  if (isoMatch) {
    return toIsoDate(
      Number(isoMatch[1]),
      Number(isoMatch[2]),
      Number(isoMatch[3])
    );
  }

  return null;
}

function parseNumber(value) {
  const originalText = clean(value);

  /*
   * Une valeur vide ne doit jamais devenir zéro.
   */
  if (!originalText || !/\d/.test(originalText)) {
    return null;
  }

  const normalizedText = originalText
    .replace(/\s/g, '')
    .replace(/[^\d,.-]/g, '')
    .replace(',', '.');

  if (!normalizedText || normalizedText === '-') {
    return null;
  }

  const number = Number(normalizedText);

  return Number.isFinite(number) ? number : null;
}

function findValue(row, aliases) {
  const normalizedAliases = aliases.map(normalizeHeader);
  const hit = Object.entries(row).find(([key]) =>
    normalizedAliases.includes(normalizeHeader(key))
  );

  return hit ? hit[1] : null;
}

// ---------------------------------------------------------------------------
// Track & Trace Excel
// ---------------------------------------------------------------------------

function normalizeTrackIdentifier(value) {
  return clean(value).replace(/\s+/g, '').toUpperCase();
}

function parseTrackNumber(value) {
  const text = clean(value);

  if (!text || !/\d/.test(text)) {
    return null;
  }

  const normalized = text
    .replace(/\s/g, '')
    .replace(/[^\d,.-]/g, '')
    .replace(',', '.');

  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

export async function parseTrackAndTrace(file) {
  const data = await file.arrayBuffer();

  const workbook = XLSX.read(data, {
    type: 'array',
    cellDates: true,
  });

  const firstSheet =
    workbook.Sheets[workbook.SheetNames[0]];

  const rows = XLSX.utils.sheet_to_json(firstSheet, {
    defval: '',
  });

  const parsed = rows
    .map((row, index) => {
      const ldv = normalizeTrackIdentifier(
        findValue(row, [
          'Liste Ordre de transport',
          'Liste Ordre de Transport',
          'Ordre de transport',
          'LDV',
          'N° LDV',
          'No LDV',
          'Lettre de voiture',
          'Numéro LDV',
          'Numero LDV',
        ])
      );

      /*
       * Dans le fichier STEF, « Référence client »
       * correspond au numéro de BL du bordereau PDF.
       */
      const blNumber = normalizeTrackIdentifier(
        findValue(row, [
          'Référence client',
          'Reference client',
          'N° BL',
          'No BL',
          'BL',
        ])
      );

      /*
       * « Etablissement » correspond au code STEF.
       */
      const stefCode = normalizeTrackIdentifier(
        findValue(row, [
          'Etablissement',
          'Établissement',
          'Code STEF',
        ])
      );

      const recipient = clean(
        findValue(row, [
          'Destinataire',
          'Nom destinataire',
          'Client',
        ])
      );

      const city = clean(
        findValue(row, [
          'Ville du destinataire',
          'Ville destinataire',
          'Ville',
        ])
      );

      const weightKg = parseTrackNumber(
        findValue(row, ['Poids', 'Poids KG', 'Poids (KG)'])
      );

      const parcelCount = parseTrackNumber(
        findValue(row, [
          'Colis',
          'Nombre de colis',
          'Nb colis',
        ])
      );

      const palletCount = parseTrackNumber(
        findValue(row, [
          'PAL.',
          'PAL',
          'Palettes',
          'Nombre de palettes',
          'Nb palettes',
        ])
      );

      const shippingDate = parseFrenchDate(
        findValue(row, [
          'Date de remise',
          "Date d'expédition",
          'Date expedition',
        ])
      );

      const estimatedDeliveryDate = parseFrenchDate(
        findValue(row, [
          'Date de livraison prévue',
          'Date livraison prévue',
          'Date de livraison prevue',
        ])
      );

      const actualDate = parseFrenchDate(
        findValue(row, [
          'Arrivé le',
          'Arrive le',
          'Date livraison',
          'Date de livraison',
          'Livré le',
          'Livre le',
        ])
      );

      const trackStatus = clean(
        findValue(row, [
          'Statut',
          'Etat',
          'État',
          'Status',
          'Etat livraison',
          'Étape',
        ])
      );

      const conformityRaw = clean(
        findValue(row, [
          'Conformité',
          'Conformite',
          'Conforme',
          'Anomalie',
        ])
      );

      let conformity = null;

      if (
        /\b(non|anomal|litige|reserve|refus)\b/i.test(
          conformityRaw
        )
      ) {
        conformity = 'non_compliant';
      } else if (
        /\b(oui|conforme|ok)\b/i.test(conformityRaw)
      ) {
        conformity = 'compliant';
      }

      return {
        row: index + 2,
        ldv,
        blNumber,
        stefCode,
        recipient,
        city,
        weightKg,
        parcelCount,
        palletCount,
        shippingDate,
        estimatedDeliveryDate,
        actualDate,
        trackStatus,
        conformity,
        finished: isFinishedTrackStatus(trackStatus),
      };
    })
    .filter((item) => item.ldv);

  if (!parsed.length) {
    throw new Error(
      'Aucune colonne « Liste Ordre de transport » ou LDV reconnue dans le fichier Track & Trace.'
    );
  }

  return parsed;
}

// ---------------------------------------------------------------------------
// Bordereaux PDF STEF
// ---------------------------------------------------------------------------

function transformPdfItem(item, viewport, pageNumber) {
  const matrix = pdfjsLib.Util.transform(viewport.transform, item.transform);
  const fontHeight = Math.hypot(matrix[2], matrix[3]) || 8;

  return {
    text: clean(item.str),
    x: matrix[4],
    y: matrix[5],
    width: Math.max(0, item.width || 0),
    height: fontHeight,
    pageNumber,
  };
}

function compareItems(first, second) {
  if (Math.abs(first.y - second.y) > LINE_TOLERANCE) {
    return first.y - second.y;
  }

  return first.x - second.x;
}

function groupIntoVisualLines(items) {
  const lines = [];

  for (const item of [...items].filter((entry) => entry.text).sort(compareItems)) {
    let closestLine = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const line of lines) {
      const distance = Math.abs(line.y - item.y);

      if (distance <= LINE_TOLERANCE && distance < closestDistance) {
        closestLine = line;
        closestDistance = distance;
      }
    }

    if (!closestLine) {
      lines.push({ y: item.y, items: [item] });
      continue;
    }

    closestLine.items.push(item);
    closestLine.y =
      closestLine.items.reduce((sum, entry) => sum + entry.y, 0) /
      closestLine.items.length;
  }

  return lines
    .map((line) => ({
      y: line.y,
      items: line.items.sort((a, b) => a.x - b.x),
      text: clean(
        line.items
          .sort((a, b) => a.x - b.x)
          .map((entry) => entry.text)
          .join(' ')
      ),
    }))
    .sort((a, b) => a.y - b.y);
}

function joinItems(items) {
  return clean(
    [...items]
      .sort(compareItems)
      .map((item) => item.text)
      .join(' ')
  );
}

function documentText(pages) {
  return pages
    .map((page) => page.lines.map((line) => line.text).join('\n'))
    .join('\n');
}

function extractDocumentMetadata(text) {
  const shippingMatch = text.match(
    /exp[ée]dition\s+du\s+(\d{1,2}[/.\-]\d{1,2}[/.\-]\d{2,4})/i
  );

  const fallbackShippingMatch = text.match(
    /date\s+de\s+remise\s*:?\s*(\d{1,2}[/.\-]\d{1,2}[/.\-]\d{2,4})/i
  );

  const positionMatch = text.match(
    /nb\s+total\s+(?:de\s+)?positions?\s*:?\s*(\d+)/i
  );

  const slipPatterns = [
    /bordereau\s+(?:n[°o]\s*)?([A-Z0-9-]{4,30})/i,
    /n[°o]\s*(?:du\s*)?bordereau\s*:?\s*([A-Z0-9-]{4,30})/i,
    /(?:n[°o]\s*)?bordereau\s*:?\s*([A-Z0-9-]{4,30})/i,
    /bordereau\s+(?:de\s+)?remise\s*:?\s*([A-Z0-9-]{4,30})/i,
    /n[°o]\s+de\s+remise\s*:?\s*([A-Z0-9-]{4,30})/i,
  ];

  let slipNumber = '';

  for (const pattern of slipPatterns) {
    const match = text.match(pattern);
    if (match) {
      slipNumber = clean(match[1]);
      break;
    }
  }

  const expectedPositionCount = positionMatch
    ? Number(positionMatch[1])
    : null;

  return {
    shippingDate: parseFrenchDate(
      shippingMatch?.[1] || fallbackShippingMatch?.[1]
    ),
    slipNumber,
    expectedPositionCount:
      Number.isInteger(expectedPositionCount) && expectedPositionCount > 0
        ? expectedPositionCount
        : null,
  };
}

function findHeaderX(page, matcher) {
  const matchingItems = page.items.filter((item) => matcher(normalize(item.text)));

  if (!matchingItems.length) return null;

  return Math.min(...matchingItems.map((item) => item.x));
}

function findHeaderGeometry(page) {
  const anchors = {
    recipient: findHeaderX(page, (text) => text.includes('destinataire')),
    codes: findHeaderX(
      page,
      (text) => text.includes('code stef') || text === 'stef'
    ),
    street: findHeaderX(page, (text) => text === 'rue' || text.includes('adresse')),
    postalCode: findHeaderX(
      page,
      (text) => text === 'cp' || text.includes('code postal')
    ),
    city: findHeaderX(page, (text) => text === 'ville'),
    parcels: findHeaderX(page, (text) => text === 'colis'),
    weight: findHeaderX(page, (text) => text.startsWith('poids')),
    transport: findHeaderX(
      page,
      (text) =>
        text === 'bl' ||
        text.endsWith(' bl') ||
        text.includes('bl / ldv') ||
        text.includes('bl/ldv')
    ),
    pallets: findHeaderX(
      page,
      (text) =>
        text === 'sup' ||
        text === 'sup 1' ||
        text === 'sup1' ||
        text.includes('support 1')
    ),
    deliveryDate: findHeaderX(
      page,
      (text) => text.includes('date livraison') || text.includes('date de livraison')
    ),
  };

  const minimumRequired = [
    anchors.recipient,
    anchors.codes,
    anchors.city,
    anchors.weight,
    anchors.transport,
    anchors.pallets,
    anchors.deliveryDate,
  ].filter((value) => value !== null).length;

  if (minimumRequired < 5) return null;

  return anchors;
}

function fallbackGeometry(pageWidth) {
  // Proportions du bordereau STEF, utilisées surtout pour les pages de suite
  // qui ne répètent pas l'en-tête du tableau.
  return {
    recipient: pageWidth * 0.026,
    codes: pageWidth * 0.117,
    street: pageWidth * 0.18,
    postalCode: pageWidth * 0.317,
    city: pageWidth * 0.362,
    parcels: pageWidth * 0.473,
    weight: pageWidth * 0.513,
    transport: pageWidth * 0.549,
    pallets: pageWidth * 0.607,
    deliveryDate: pageWidth * 0.736,
  };
}

function completeGeometry(geometry, pageWidth) {
  const fallback = fallbackGeometry(pageWidth);
  const completed = {};

  for (const key of Object.keys(fallback)) {
    completed[key] = geometry?.[key] ?? fallback[key];
  }

  const orderedKeys = [
    'recipient',
    'codes',
    'street',
    'postalCode',
    'city',
    'parcels',
    'weight',
    'transport',
    'pallets',
    'deliveryDate',
  ];

  for (let index = 1; index < orderedKeys.length; index += 1) {
    const previousKey = orderedKeys[index - 1];
    const key = orderedKeys[index];

    if (completed[key] <= completed[previousKey]) {
      completed[key] = fallback[key];
    }
  }

  return completed;
}

function columnRanges(geometry, pageWidth) {
  const starts = [
    ['recipient', geometry.recipient],
    ['codes', geometry.codes],
    ['street', geometry.street],
    ['postalCode', geometry.postalCode],
    ['city', geometry.city],
    ['parcels', geometry.parcels],
    ['weight', geometry.weight],
    ['transport', geometry.transport],
    ['pallets', geometry.pallets],
    ['deliveryDate', geometry.deliveryDate],
  ].sort((a, b) => a[1] - b[1]);

  const ranges = {};
  const leftTolerance = pageWidth * 0.006;

  starts.forEach(([key, start], index) => {
    const nextStart = starts[index + 1]?.[1] ?? pageWidth;

    ranges[key] = {
      // Les coordonnées d'un item PDF correspondent au début de son texte.
      // Les débuts de colonnes sont donc de meilleures limites que leurs
      // milieux, notamment pour les destinataires et villes assez longs.
      min: index === 0 ? 0 : Math.max(0, start - leftTolerance),
      max:
        index === starts.length - 1
          ? pageWidth
          : Math.max(start, nextStart - leftTolerance),
    };
  });

  return ranges;
}

function itemsInRange(items, range) {
  return items.filter((item) => item.x >= range.min && item.x < range.max);
}

function compactTokens(value) {
  return clean(value)
    .split(/\s+/)
    .map((token) => token.replace(/^[^A-Z0-9]+|[^A-Z0-9/_-]+$/gi, ''))
    .filter(Boolean);
}

function extractLdv(value) {
  const compact = clean(value).replace(/\s+/g, '').toUpperCase();
  // Une lettre de voiture STEF contient toujours « 0V » suivi de 10 chiffres.
  // Une longueur variable absorbait parfois le nombre de palettes placé juste
  // après la LDV (ex. 0V4260393151 + 1 PA devenait 0V426039315111).
  const match = compact.match(/0V\d{10}/);
  return match ? match[0] : '';
}

function findLdvAnchors(page, transportRange) {
  const candidates = [];

  for (const line of page.lines) {
    const transportItems = itemsInRange(line.items, transportRange);
    const transportText = joinItems(transportItems);
    const ldv = extractLdv(transportText);

    if (ldv) {
      const ldvItem =
        transportItems.find((item) => extractLdv(item.text)) ||
        transportItems[0];

      candidates.push({
        y: line.y,
        x: ldvItem?.x ?? null,
        ldv,
      });
      continue;
    }

    // Repli : certains PDF fractionnent 0V et les chiffres en deux blocs.
    const fullLineLdv = extractLdv(line.text);
    if (fullLineLdv) {
      const ldvItem =
        line.items.find((item) => /0V/i.test(item.text)) ||
        line.items.find((item) => /\d{8,15}/.test(item.text));

      candidates.push({
        y: line.y,
        x: ldvItem?.x ?? null,
        ldv: fullLineLdv,
      });
    }
  }

  return candidates.filter(
    (candidate, index, values) =>
      values.findIndex((value) => value.ldv === candidate.ldv) === index
  );
}

function buildRowBands(page, ldvAnchors, deliveryDateRange) {
  const datePattern = /\b\d{1,2}[/.-]\d{1,2}[/.-](?:\d{2}|\d{4})\b/;

  /*
   * La date de livraison est toujours placée sur la première ligne visuelle
   * d'une position. Elle constitue donc un début de ligne plus fiable que la
   * LDV, qui peut descendre d'une ou deux lignes lorsque le BL est long.
   */
  const rowStarts = ldvAnchors.map((anchor) => {
    const dateLine = page.lines
      .filter(
        (line) =>
          line.y <= anchor.y + LINE_TOLERANCE &&
          line.y >= anchor.y - 28 &&
          datePattern.test(
            joinItems(itemsInRange(line.items, deliveryDateRange))
          )
      )
      .sort((first, second) => second.y - first.y)[0];

    return dateLine?.y ?? anchor.y - 10;
  });

  return ldvAnchors.map((anchor, index) => {
    const nextStart = rowStarts[index + 1];
    const minY = rowStarts[index] - LINE_TOLERANCE;
    const maxY = nextStart === undefined
      ? anchor.y + LINE_TOLERANCE + 1
      : nextStart - LINE_TOLERANCE;

    return {
      ...anchor,
      minY,
      maxY,
      items: page.items.filter((item) => item.y >= minY && item.y < maxY),
    };
  });
}

function extractCodes(row, fallbackValue = '') {
  const rowTokens = row.items.flatMap((item) =>
    compactTokens(item.text).map((token) => ({
      token: token.toUpperCase(),
      x: item.x,
      y: item.y,
    }))
  );

  const stefEntry = rowTokens.find((entry) =>
    /^(?:\d{6}|\d{2}[A-Z]\d{3})$/.test(entry.token)
  );

  if (!stefEntry) {
    return {
      stefCode: '',
      customerCode: '',
    };
  }

  const pageWidth = Math.max(
    1,
    ...row.items.map(
      (item) => item.x + Math.max(item.width || 0, 1)
    )
  );

  /*
   * Le code client se trouve sous le code STEF et commence
   * pratiquement au même emplacement horizontal.
   */
  const horizontalTolerance = pageWidth * 0.035;

  const addressWords = new Set([
    'RUE',
    'ROUTE',
    'AVENUE',
    'CHEMIN',
    'IMPASSE',
    'PLACE',
    'QUAI',
    'ZONE',
    'LES',
    'LE',
    'LA',
    'LIEU',
    'DIT',
    'IMMEUBLE',
    'BATIMENT',
  ]);

  const customerEntry = rowTokens
    .filter(
      (entry) =>
        entry.token !== stefEntry.token &&
        /^[A-Z0-9][A-Z0-9_-]{2,19}$/.test(entry.token) &&
        !addressWords.has(entry.token) &&
        Math.abs(entry.x - stefEntry.x) <=
          horizontalTolerance
    )
    .map((entry) => ({
      ...entry,
      score:
        Math.abs(entry.x - stefEntry.x) * 4 +
        Math.abs(entry.y - stefEntry.y),
    }))
    .sort((first, second) => first.score - second.score)[0];

  /*
   * Repli limité à la véritable colonne des codes.
   */
  const fallbackTokens = compactTokens(fallbackValue)
    .map((token) => token.toUpperCase())
    .filter(
      (token) =>
        token !== stefEntry.token &&
        /^[A-Z0-9][A-Z0-9_-]{2,19}$/.test(token) &&
        !addressWords.has(token)
    );

  return {
    stefCode: stefEntry.token,
    customerCode:
      customerEntry?.token || fallbackTokens[0] || '',
  };
}

function extractTransport(value, knownLdv) {
  const compact = clean(value).replace(/\s+/g, ' ').toUpperCase();
  const ldvNumber = knownLdv || extractLdv(compact);
  const withoutLdv = compact.replace(ldvNumber, ' ');
  const candidates = compactTokens(withoutLdv);

  const blNumber =
    candidates.find((token) => /^\d{3,}(?:\/\d+)*$/.test(token)) || '';

  return { blNumber, ldvNumber };
}

function extractBlNearLdv(row, knownLdv, fallbackTransportText) {
  const direct = extractTransport(fallbackTransportText, knownLdv);

  const pageWidth = Math.max(
    1,
    ...row.items.map((item) => item.x + Math.max(item.width, 1))
  );
  const horizontalTolerance = pageWidth * 0.065;

  const candidates = rankStefBlCandidates(row.items
    .flatMap((item) => {
      const tokens = compactTokens(item.text);

      return tokens.map((token, tokenIndex) => ({
        token: token.toUpperCase(),
        tokenIndex,
        tokenCount: tokens.length,
        x: item.x,
        y: item.y,
      }));
    })
    .filter(
      (candidate) =>
        /^\d{4,}(?:\/\d+)*$/.test(candidate.token) &&
        !knownLdv.includes(candidate.token)
    )
    .filter(
      (candidate) =>
        row.x === null || Math.abs(candidate.x - row.x) <= horizontalTolerance
    ), row);

  const baseBlNumber = candidates[0]?.token || direct.blNumber || '';
  const baseEntry = row.items
    .flatMap((item) =>
      compactTokens(item.text).map((token) => ({
        token: token.toUpperCase(),
        x: item.x,
        y: item.y,
      }))
    )
    .find((entry) => entry.token === baseBlNumber);

  /*
   * Un BL peut être coupé au milieu d'un numéro. Exemple réel :
   * « 60445/446/205 » puis « 71 » sur la ligne suivante doit produire
   * « 60445/446/20571 » (et non « .../205/71 »). Autre cas validé :
   * « 60794/20693/1 » puis « 9821 » devient « 60794/20693/19821 ».
   */
  const continuations = baseEntry
    ? row.items
        .flatMap((item) =>
          compactTokens(item.text).map((token) => ({
            token,
            x: item.x,
            y: item.y,
          }))
        )
        .filter(
          (entry) =>
            /^\d{1,5}$/.test(entry.token) &&
            entry.y > baseEntry.y + LINE_TOLERANCE &&
            entry.y < row.y - LINE_TOLERANCE &&
            (row.x === null ||
              Math.abs(entry.x - row.x) <= horizontalTolerance)
        )
        .sort((first, second) => first.y - second.y || first.x - second.x)
        .map((entry) => entry.token)
    : [];

  return {
    blNumber: `${baseBlNumber}${continuations.join('')}`,
    ldvNumber: knownLdv,
  };
}

function extractPalletCount(value) {
  const text = clean(value);
  const explicit = text.match(/\b(\d+)\s*PA\b/i);
  const fallback = text.match(/\b(\d+)\b/);
  const number = Number(explicit?.[1] || fallback?.[1]);

  return Number.isInteger(number) && number > 0 ? number : null;
}

function extractDeliveryDateFromRow(value, shippingDate) {
  const matches = [
    ...clean(value).matchAll(
      /\b\d{1,2}[/.\-]\d{1,2}[/.\-](?:\d{2}|\d{4})\b/g
    ),
  ];

  const dates = matches
    .map((match) => parseFrenchDate(match[0]))
    .filter(Boolean);

  return dates.find((date) => date !== shippingDate) || dates[0] || null;
}

function extractFirstInteger(value) {
  const match = clean(value).match(/\b(\d+)\b/);
  return match ? Number(match[1]) : null;
}

function extractFirstDate(value) {
  const match = clean(value).match(
    /\b\d{1,2}[/.\-]\d{1,2}[/.\-](?:\d{2}|\d{4})\b/
  );
  return match ? parseFrenchDate(match[0]) : null;
}

function cleanRecipient(value) {
  return clean(value)
    .replace(/\s+-\s+N\b/gi, ' ')
    .replace(/\bN\s*$/i, '')
    .trim();
}

function extractCityFromRow(row, fallbackValue = '') {
  const pageWidth = Math.max(
    1,
    ...row.items.map(
      (item) => item.x + Math.max(item.width || 0, 1)
    )
  );

  const visualLines = groupIntoVisualLines(row.items);

  /*
   * Recherche du code postal. Le BL peut également avoir
   * cinq chiffres, mais il se trouve beaucoup plus près de la LDV.
   */
  const postalCandidates = row.items
    .flatMap((item) =>
      compactTokens(item.text).map((token) => ({
        token,
        x: item.x,
        y: item.y,
      }))
    )
    .filter(
      (entry) =>
        /^\d{5}$/.test(entry.token) &&
        (
          row.x === null ||
          entry.x < row.x - pageWidth * 0.08
        )
    )
    .sort((first, second) => second.x - first.x);

  const postalCode = postalCandidates[0];

  if (!postalCode) {
    return clean(fallbackValue);
  }

  /*
   * Retrouve la ligne visuelle contenant le code postal.
   */
  const postalLine = [...visualLines].sort(
    (first, second) =>
      Math.abs(first.y - postalCode.y) -
      Math.abs(second.y - postalCode.y)
  )[0];

  if (!postalLine) {
    return clean(fallbackValue);
  }

  /*
   * Le premier petit nombre situé à droite du code postal
   * correspond au nombre de colis.
   */
  const parcelCandidate = postalLine.items
    .flatMap((item) =>
      compactTokens(item.text).map((token) => ({
        token,
        x: item.x,
      }))
    )
    .filter(
      (entry) =>
        /^\d{1,3}$/.test(entry.token) &&
        entry.x > postalCode.x + pageWidth * 0.06
    )
    .sort((first, second) => first.x - second.x)[0];

  if (!parcelCandidate) {
    return clean(fallbackValue);
  }

  const cityMinimumX =
    postalCode.x + pageWidth * 0.025;

  const cityMaximumX =
    parcelCandidate.x - pageWidth * 0.005;

  const city = joinItems(
    row.items.filter(
      (item) =>
        item.x >= cityMinimumX &&
        item.x < cityMaximumX
    )
  );

  return clean(city) || clean(fallbackValue);
}

function findBlItemContext(row, blNumber) {
  if (!blNumber) return null;

  const matches = row.items
    .map((item) => {
      const tokens = compactTokens(item.text);
      const blIndex = tokens.findIndex(
        (token) =>
          /^\d{3,}(?:\/\d+)*$/.test(token) &&
          blNumber.startsWith(token)
      );

      if (blIndex < 0) return null;

      const inlineNumber = [...tokens.slice(0, blIndex)]
        .reverse()
        .find((token) => /^\d+(?:[.,]\d+)?$/.test(token));

      return {
        item,
        blToken: tokens[blIndex],
        inlineNumber: inlineNumber || null,
      };
    })
    .filter(Boolean)
    .sort((first, second) => second.blToken.length - first.blToken.length);

  return matches[0] || null;
}

function extractWeightNearBl(
  row,
  blNumber,
  fallbackValue = ''
) {
  if (!blNumber) {
    return parseNumber(fallbackValue);
  }

  const blContext = findBlItemContext(row, blNumber);

  /*
   * PDF.js regroupe parfois « poids + BL » dans un seul bloc texte,
   * par exemple « 222 60445/446/205 ». Dans ce cas, le nombre placé avant
   * le BL dans ce bloc est la valeur de poids fiable.
   */
  if (blContext?.inlineNumber) {
    return parseNumber(blContext.inlineNumber);
  }

  const visualLines = groupIntoVisualLines(row.items);

  const blEntries = row.items.flatMap((item) =>
    compactTokens(item.text).map((token) => ({
      token: token.toUpperCase(),
      x: item.x,
      y: item.y,
    }))
  );

  const blEntry = blEntries.find(
    (entry) => entry.token === blNumber.toUpperCase()
  );

  if (!blEntry) {
    return parseNumber(fallbackValue);
  }

  /*
   * Recherche de la ligne visuelle contenant le BL.
   */
  const blLine = [...visualLines].sort(
    (first, second) =>
      Math.abs(first.y - blEntry.y) -
      Math.abs(second.y - blEntry.y)
  )[0];

  if (!blLine) {
    return parseNumber(fallbackValue);
  }

  /*
   * Sur le bordereau STEF, la séquence est :
   * ville | colis | poids | BL.
   *
   * Le nombre placé immédiatement à gauche du BL est donc
   * le poids.
   */
  const numericValuesBeforeBl = blLine.items
    .flatMap((item) =>
      compactTokens(item.text).map((token) => ({
        token,
        x: item.x,
      }))
    )
    .filter(
      (entry) =>
        entry.x < blEntry.x &&
        /^\d+(?:[.,]\d+)?$/.test(entry.token)
    )
    .sort((first, second) => second.x - first.x);

  const closestNumber = numericValuesBeforeBl[0];

  return (
    parseNumber(closestNumber?.token) ??
    parseNumber(fallbackValue)
  );
}

function extractParcelCountNearBl(
  row,
  blNumber,
  fallbackValue = ''
) {
  if (!blNumber) {
    return extractFirstInteger(fallbackValue);
  }

  const blContext = findBlItemContext(row, blNumber);

  const visualLines = groupIntoVisualLines(row.items);

  const blEntries = row.items.flatMap((item) =>
    compactTokens(item.text).map((token) => ({
      token: token.toUpperCase(),
      x: item.x,
      y: item.y,
    }))
  );

  const blEntry = blEntries.find(
    (entry) => entry.token === blNumber.toUpperCase()
  );

  if (!blEntry) {
    return extractFirstInteger(fallbackValue);
  }

  const blLine = [...visualLines].sort(
    (first, second) =>
      Math.abs(first.y - blEntry.y) -
      Math.abs(second.y - blEntry.y)
  )[0];

  if (!blLine) {
    return extractFirstInteger(fallbackValue);
  }

  const numbersBeforeBl = blLine.items
    .flatMap((item) =>
      compactTokens(item.text).map((token) => ({
        token,
        x: item.x,
      }))
    )
    .filter(
      (entry) =>
        entry.x < blEntry.x &&
        /^\d+(?:[.,]\d+)?$/.test(entry.token)
    )
    .sort((first, second) => second.x - first.x);

  /*
   * numbersBeforeBl[0] = poids
   * numbersBeforeBl[1] = nombre de colis
   */
  const parcelValue = blContext?.inlineNumber
    ? numbersBeforeBl[0]?.token
    : numbersBeforeBl[1]?.token;

  return (
    extractFirstInteger(parcelValue) ??
    extractFirstInteger(fallbackValue)
  );
}

function parseShipmentRow(row, ranges, metadata) {
  const completeRowText = joinItems(row.items);
  const recipientText = joinItems(itemsInRange(row.items, ranges.recipient));
  const codesText = joinItems(itemsInRange(row.items, ranges.codes));
  const cityText = joinItems(itemsInRange(row.items, ranges.city));
  const parcelText = joinItems(itemsInRange(row.items, ranges.parcels));
  const weightText = joinItems(itemsInRange(row.items, ranges.weight));
  const transportText = joinItems(itemsInRange(row.items, ranges.transport));
  const palletText = joinItems(itemsInRange(row.items, ranges.pallets));
  const deliveryDateText = joinItems(
    itemsInRange(row.items, ranges.deliveryDate)
  );

 const { stefCode, customerCode } = extractCodes(
  row,
  codesText
);
  const { blNumber, ldvNumber } = extractBlNearLdv(
    row,
    row.ldv,
    transportText
  );

  return {
    customer_code: customerCode,
    stef_code: stefCode,
    recipient: cleanRecipient(recipientText),
    city: extractCityFromRow(row, cityText),
    // Ces trois valeurs sont volontairement recherchées dans toute la ligne.
    // Les bordereaux STEF peuvent légèrement déplacer les colonnes selon
    // l'imprimante PDF, mais les marqueurs PA, LDV et date restent stables.
    pallet_count:
      extractPalletCount(palletText) || extractPalletCount(completeRowText),
    weight_kg: extractWeightNearBl(row, blNumber, weightText),
    bl_number: blNumber,
    ldv_number: ldvNumber,
    shipping_date: metadata.shippingDate,
    estimated_delivery_date:
      extractFirstDate(deliveryDateText) ||
      extractDeliveryDateFromRow(completeRowText, metadata.shippingDate),
    status: 'in_delivery',
    source: 'pdf',

    // Informations utiles au contrôle de l'import. App.jsx ne doit envoyer
    // à Supabase que les colonnes qui existent dans la table shipments.
    slip_number: metadata.slipNumber || null,
    pdf_page: row.items[0]?.pageNumber || null,
    parcel_count: extractParcelCountNearBl(row, blNumber, parcelText),
  };
}

function missingRequiredFields(shipment) {
  const required = [
    'customer_code',
    'stef_code',
    'recipient',
    'city',
    'pallet_count',
    'bl_number',
    'ldv_number',
    'shipping_date',
    'estimated_delivery_date',
  ];

  return required.filter((field) => {
    const value = shipment[field];
    return value === null || value === undefined || value === '';
  });
}

function assertUniqueLdvs(shipments) {
  const seen = new Set();
  const duplicates = new Set();

  for (const shipment of shipments) {
    if (seen.has(shipment.ldv_number)) duplicates.add(shipment.ldv_number);
    seen.add(shipment.ldv_number);
  }

  if (duplicates.size) {
    throw new Error(
      `Plusieurs lignes du bordereau possèdent la même LDV : ${[
        ...duplicates,
      ].join(', ')}.`
    );
  }
}

function assertCompleteShipments(shipments) {
  const invalid = shipments
    .map((shipment, index) => ({
      position: index + 1,
      page: shipment.pdf_page,
      missing: missingRequiredFields(shipment),
    }))
    .filter((entry) => entry.missing.length);

  if (!invalid.length) return;

  const details = invalid
    .map(
      (entry) =>
        `position ${entry.position}${entry.page ? `, page ${entry.page}` : ''} : ${entry.missing.join(', ')}`
    )
    .join(' | ');

  throw new Error(`Certaines lignes sont incomplètes. ${details}`);
}

export async function parseStefPdf(file) {
  const pdf = await pdfjsLib.getDocument({
    data: await file.arrayBuffer(),
  }).promise;

  const pages = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1, rotation: page.rotate });
    const content = await page.getTextContent();

    const items = content.items
      .map((item) => transformPdfItem(item, viewport, pageNumber))
      .filter((item) => item.text);

    pages.push({
      pageNumber,
      width: viewport.width,
      height: viewport.height,
      items,
      lines: groupIntoVisualLines(items),
    });
  }

  const rawText = documentText(pages);
  const metadata = extractDocumentMetadata(rawText);

  if (!metadata.shippingDate) {
    throw new Error(
      "La date commune « Expédition du … » n'a pas été reconnue dans le bordereau."
    );
  }

  const pageWithHeader = pages.find((page) => findHeaderGeometry(page));
  const detectedGeometry = pageWithHeader
    ? findHeaderGeometry(pageWithHeader)
    : null;

  const shipments = [];

  for (const page of pages) {
    const geometry = completeGeometry(
      findHeaderGeometry(page) || detectedGeometry,
      page.width
    );
    const ranges = columnRanges(geometry, page.width);
    const anchors = findLdvAnchors(page, ranges.transport);
    const rows = buildRowBands(page, anchors, ranges.deliveryDate);

    for (const row of rows) {
      shipments.push(parseShipmentRow(row, ranges, metadata));
    }
  }

  if (!shipments.length) {
    throw new Error(
      "Aucune livraison n'a été reconnue. Vérifiez que le PDF contient une couche texte et qu'il s'agit bien du bordereau de livraison attendu."
    );
  }

  if (
    metadata.expectedPositionCount !== null &&
    shipments.length !== metadata.expectedPositionCount
  ) {
    throw new Error(
      `${metadata.expectedPositionCount} position(s) annoncée(s), mais ${shipments.length} livraison(s) reconnue(s). L'import est annulé pour éviter une création incomplète.`
    );
  }

  assertUniqueLdvs(shipments);
  assertCompleteShipments(shipments);

  return {
    shipments,
    expectedPositionCount: metadata.expectedPositionCount,
    slipNumber: metadata.slipNumber || null,
    raw_text: rawText,
  };
}

// ---------------------------------------------------------------------------
// Preuves de livraison (POD)
// ---------------------------------------------------------------------------

export async function parsePodPdf(file) {
  const pdf = await pdfjsLib.getDocument({
    data: await file.arrayBuffer(),
  }).promise;
  const pages = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1, rotation: page.rotate });
    const content = await page.getTextContent();
    const items = content.items
      .map((item) => transformPdfItem(item, viewport, pageNumber))
      .filter((item) => item.text);

    pages.push({
      pageNumber,
      width: viewport.width,
      height: viewport.height,
      items,
    });
  }

  return parsePodPages(pages);
}

// Exporté uniquement pour faciliter des tests unitaires futurs.
export const __testables = {
  parseFrenchDate,
  extractDocumentMetadata,
  extractLdv,
  extractCodes,
  extractTransport,
  extractPalletCount,
  missingRequiredFields,
  DAY_MS,
};
